import React from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDownIcon } from '@/shared/assets/icons/chevron-down-icon';
import { ChevronUpIcon } from '@/shared/assets/icons/chevron-up-icon';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { PopoverController } from '@/shared/components/ui/popover/components/popover-controller/popover-controller';
import { Tooltip } from '@/shared/components/ui/tooltip/tooltip';
import { LANGUAGE_OPTIONS } from '@/shared/features/language-switcher/language-switcher.constants';
import {
  LanguageSwitcherMenuItem,
  LanguageSwitcherSelect,
} from '@/shared/features/language-switcher/language-switcher.styled';
import { TLanguageSwitcherProps } from '@/shared/features/language-switcher/language-switcher.types';

export const LanguageSwitcher: React.FC<TLanguageSwitcherProps> = ({
  component,
  onLanguageChange,
  compact,
}) => {
  const { i18n, t } = useTranslation();

  const getSelectedLanguageIcon = () => {
    return LANGUAGE_OPTIONS.find((lang) => lang.code === i18n.language)?.flag;
  };

  const getTriggerComponent = (open: boolean) => {
    if (component === 'MenuItem') {
      return (
        <Tooltip
          title={t(`ui:language.${i18n.language}`)}
          disableHoverListener={!compact}
        >
          <LanguageSwitcherMenuItem
            icon={getSelectedLanguageIcon()}
            alignLabelLeft
          >
            {!compact && t(`ui:language.${i18n.language}`)}
          </LanguageSwitcherMenuItem>
        </Tooltip>
      );
    }

    return (
      <LanguageSwitcherSelect
        variant="secondary"
        size="large"
        icon={getSelectedLanguageIcon()}
        tooltip={t(`ui:language.${i18n.language}`)}
      >
        {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </LanguageSwitcherSelect>
    );
  };

  return (
    <PopoverController
      trigger={(popoverState) => getTriggerComponent(popoverState)}
    >
      {(popoverClose) =>
        LANGUAGE_OPTIONS.map((lang) => (
          <MenuItem
            key={lang.code}
            value={lang.code}
            onClick={() => {
              void i18n.changeLanguage(lang.code);
              onLanguageChange?.(lang.code);
              popoverClose();
            }}
            selected={lang.code === i18n.language}
            icon={lang.flag}
          >
            {t(`ui:language.${lang.code}`)}
          </MenuItem>
        ))
      }
    </PopoverController>
  );
};
